<template lang="pug">
div
  v-card.round-xl.pa-5.mt-3(outlined)
    div.d-flex.mb-1(v-if="history.company && history.company.slug !== company.slug")
      yz-badge.pa-1(border size="sm" border-color="blue-500" text-color="blue-500" color="blue-100") {{ history.company.name }}
    v-row(no-gutters style="gap: 12px;")
      v-col.shrink(style="min-width: 100px;")
        div.text-xs.font-semibold.neutral700--text {{ history.record_date | humanizeDate }}
        div.text-xs.neutral700--text.mt-1 {{ history.record_date | displayTime2 }}
        template(v-if="history.provider")
          y-chip.mt-3(:label="history.provider.name" size="xs" chip-class="pa-1" color="blue" truncated)
            template(#prepend)
              v-avatar.mr-1(size="16" color="neutral300")
                template(v-if="history.provider.profile_image")
                  v-img(:src="`https://img.yezza.io/50x50/${history.provider.profile_image.path}`")
                v-icon(v-else color="neutral500" size="15") $yzi-user-03
      v-divider(vertical)
      v-col.grow
        div.text-sm.neutral700--text.ellipsis-text-2(v-if="history.consultation && history.consultation.case") {{ history.consultation.case }}
        div.text-sm.neutral700--text(v-else) -
        v-row.mt-3(no-gutters style="gap: 8px; align-items: center")
          v-col.shrink
            div.text-xs.neutral700--text Diagnosis:
          v-col.grow
            yz-truncated-list(v-if="history.consultation && history.consultation.diagnoses.length" :items="history.consultation.diagnoses" :gap="8")
            div.text-xs.neutral700--text(v-else) -
          v-col.shrink
            y-button(outlined color="neutral400" prepend-icon="mdi-chevron-right" icon-color="neutral500" icon-size="20" unset-min-width size="xxs" height="24" width="24" @click="showDialog = true")

  .dialogs
    y-dialog(v-if="showDialog" v-model="showDialog" max-width="740" persistent fluid scrollable content-class="round-2xl")
      v-card
        v-card-title.px-4.py-3
          v-row(no-gutters align="center")
            v-col(cols="10" offset="1")
              .text-md.font-semibold.text-center Patient History
            v-col(cols="1" align="right")
              v-btn(icon @click="showDialog = false" small color="primary")
                v-icon mdi-close
        v-divider
        v-card-text.px-6
          patient-history-item(:patient-history.sync="history" @updated="onUpdated")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PatientHistoryItem from '@/components/clinic/consultation/PatientHistoryItem.vue'

export default {
  components: { PatientHistoryItem },
  props: {
    patientHistory: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      isLoading: false,
      addTreatmentMode: false,
      showDialog: false,
      showDeleteDialog: false,
      selectedFile: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      patient: 'treatment/getPatient',
      registration: 'treatment/getRegistration',
      activeMedications: 'treatment/getActiveMedications',
    }),
    history: {
      get () {
        return this.patientHistory
      },
      set (value) {
        this.$emit('update:patientHistory', value)
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchTreatments () {
      this.isFetching = true
      let params = {
        fields: 'treatments'
      }
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, { params })
        .then(resp => {
          this.$store.commit('treatment/setTreatments', resp.data.treatments)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    getTreatmentExtraData (extra_data) {
      let data = []
      if (extra_data.indication) { data.push(extra_data.indication) }
      if (extra_data.dosage_value && extra_data.dosage_unit) { data.push(`${extra_data.dosage_value} ${extra_data.dosage_unit}`) }
      if (extra_data.frequency) { data.push(extra_data.frequency) }
      if (extra_data.duration) { data.push(extra_data.duration) }
      if (extra_data.instruction) { data.push(extra_data.instruction) }
      if (extra_data.precaution) { data.push(extra_data.precaution) }
      return data.join(' | ')
    },
    getAppointmentServices (appointment) {
      let services = []
      if (appointment.services.length) {
        services = appointment.services.map(s => s.name)
      }
      return services.join(', ')
    },
    checkActiveMadication (treatment) {
      if (treatment.product.medication && treatment.product.medication) {
        if (this.activeMedications.find(f => f.m_id === treatment.product.medication.id)) {
          return true
        }
      }
      return false
    },
    checkMedicine (treatment) {
      if (treatment.product.medication && treatment.product.medication.id) {
        return true
      }
      return false
    },
    deleteItem (item) {
      this.showDeleteDialog = true
      this.selectedFile = item
    },
    downloadFile (item) {
      window.open(item.document.file, '_blank')
    },
    viewFile (item) {
      if (item.document.type.includes('image')) {
        this.viewImage(item.document.file)
      } else if (item.document.type.includes('pdf')) {
        this.viewPDF(item.document.file)
      }
    },
    viewImage (image) {
      this.selectedFile = image
      this.showViewImageDialog = true
    },
    viewPDF (file) {
      const url = `https://docs.google.com/viewer?url=${file}`
      window.open(url,'_blank')
    },
    handleDeletedItem () {
      this.selectedFile = null
      this.$emit('updated')
    },
    async addPastTreatment () {
      this.isLoading = true
      let products = []
      let treatments = this.history.treatments.filter(f => f.selected)
      if (treatments.length === 0) {
        this.showMessage(['error', 'Please select at least one item'])
        this.isLoading = false
        return
      }
      for (let i = 0; i < treatments.length; i++) {
        let data = {
          product: treatments[i].product.id,
          quantity: 1,
          price: 0
        }
        let params = { 
          company: this.company.id,
          product: treatments[i].product.id
        }
        if (this.registration?.default_payment?.type !== 'self-pay') {
          params.payment_methods = this.registration.default_payment.panel.id
        } else {
          params.payment_channel = 'self-pay'
        }
        await this.$axios.get('/product_pricetiers', { params })
          .then(resp => {
            if (resp.data.count) {
              data.price = resp.data.results[0].price || 0
              data.extra_data = {
                price_tier: resp.data.results[0].id,
                price_tier_name: resp.data.results[0].name,
              }
            }
          })
          .catch(() => {})
        if (this.checkMedicine(treatments[i])) {
          data.extra_data = {
            ...data.extra_data,
            dosage_unit: treatments[i].product.medication.dosage_unit,
            dosage_value: treatments[i].product.medication.dosage_value,
            duration: treatments[i].product.medication.duration,
            frequency: treatments[i].product.medication.frequency,
            indication: treatments[i].product.medication.indication,
            instruction: treatments[i].product.medication.instruction,
            precaution: treatments[i].product.medication.precaution,
          }
        }
        products.push(data)
        treatments[i].selected = false
      }
      let payload = { products: products }
      const endpoint = `/leads/${this.leadPid}`
      await this.$axios.patch(endpoint, payload)
        .then(resp2 => {
          this.showMessage(['success', 'Items added to treatment plan'])
          this.updateActivityLog(endpoint, payload, resp2.data, 'PATCH', resp2.data.id, 'consultation', `Update treatment plan from patient history (${this.leadPid})`)
          this.fetchTreatments()
        })
        .catch((err) => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.status === 400 && err.response?.data?.code === '444101') {
            message = 'Item quantity more than available stock. Please adjust the quantity'
          }       
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        })
      this.isLoading = false
      this.addTreatmentMode = false
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    onUpdated () {
      this.$emit('updated')
    },
  },
}
</script>

<style lang="scss" scoped>

</style>